import React, { useEffect, useMemo, useRef, useState } from 'react';
import { auth, db } from '../../../firebase/config';
import { doc, updateDoc, collection, getDocs, deleteDoc, addDoc, Timestamp } from 'firebase/firestore';
import { useAppSelector } from '../../../store';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiEdit2, FiTrash2, FiUpload, FiDownload, FiLayers, FiCheckCircle, FiXCircle, FiFilter, FiX, FiChevronDown, FiFileText, FiUsers, FiShield } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import AddBranchModal from '../components/AddBranchModal';
import BranchDetailsModal from '../components/BranchDetailsModal';
import DeactivationModal from '../components/DeactivationModal';
import Modal from '../../../components/Modal';
import { fetchBranchesData, fetchBranchBankId, type Branch } from '../service/branchesService';
import {
    fetchApprovedTemporaryGuardLookupForBank,
    lookupTemporaryGuardCount,
    type TemporaryGuardBranchLookup,
} from '../../request_management/request_form/service/requestManagementService';
import { BRANCH_TYPE_OPTIONS } from '../components/AddBranchModal/service/addBranchService';
import * as XLSX from 'xlsx';
import Table, { Column } from '../../../components/Table';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { designationIsIncharge } from '../../../utils/designationRole';

type Manager = {
    id: string;
    bankId: string;
    name: string;
    email: string;
    mobile: string;
    type?: string;
};

type BranchTypeKpiRow = { label: string; count: number };

function branchTypeKpiLabel(branch: Pick<Branch, 'branchType' | 'branchTypeOther'>): string {
    const t = String(branch.branchType || 'Branch').trim() || 'Branch';
    if (t === 'Other' && String(branch.branchTypeOther || '').trim()) {
        return `${t} — ${String(branch.branchTypeOther).trim()}`;
    }
    return t;
}

function countBranchesByType(branches: Pick<Branch, 'branchType' | 'branchTypeOther'>[]): Record<string, number> {
    const byType: Record<string, number> = {};
    for (const b of branches) {
        const label = branchTypeKpiLabel(b);
        byType[label] = (byType[label] || 0) + 1;
    }
    return byType;
}

function branchTypeKpiOrderedRows(counts: Record<string, number>): BranchTypeKpiRow[] {
    const rows: BranchTypeKpiRow[] = [];
    const consumed = new Set<string>();

    for (const opt of BRANCH_TYPE_OPTIONS) {
        if (opt === 'Other') {
            let c = counts['Other'] || 0;
            for (const k of Object.keys(counts)) {
                if (k.startsWith('Other —')) {
                    c += counts[k] || 0;
                    consumed.add(k);
                }
            }
            consumed.add('Other');
            rows.push({ label: 'Other', count: c });
        } else {
            rows.push({ label: opt, count: counts[opt] || 0 });
            consumed.add(opt);
        }
    }

    const extras = Object.keys(counts)
        .filter((k) => !consumed.has(k))
        .sort((a, b) => a.localeCompare(b))
        .map((k) => ({ label: k, count: counts[k] || 0 }));

    rows.push(...extras);
    return rows;
}

function branchTableGeo(b: Branch): { lat: string; lng: string; distance: string } {
    let lat = '';
    let lng = '';
    let distance = '';
    if (b.location && (b.location.lat != null || b.location.long != null)) {
        if (b.location.lat != null) lat = String(b.location.lat);
        if (b.location.long != null) lng = String(b.location.long);
        distance = (b.location.distanceFromRegion || '').trim();
    }
    if ((!lat || !lng) && b.latitudeLongitude) {
        const parts = b.latitudeLongitude.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
        if (parts.length >= 2) {
            lat = lat || parts[0];
            lng = lng || parts[1];
        }
    }
    if (!distance && b.distanceFromRegion) distance = String(b.distanceFromRegion).trim();
    return { lat, lng, distance };
}

/** Same text shape as the Branch table column (code, name, mobile). */
function branchTableIdentityLine(b: Branch): string {
    const safe = (v: any) => (v == null ? '' : String(v));
    const parts = [
        `Code ${safe(b.branchCode).trim() || '—'}`,
        safe(b.address).trim() || '—',
        safe(b.mobile).trim() ? `Branch Contact No. ${safe(b.mobile).trim()}` : '',
    ].filter(Boolean);
    return parts.join(' | ');
}

/** Same text shape as the Location & position table column (region, lat/long, distance). */
function branchTableLocationLine(b: Branch): string {
    const g = branchTableGeo(b);
    const coord =
        g.lat && g.lng ? `${Number(g.lat).toFixed(5)}, ${Number(g.lng).toFixed(5)}` : '—';
    const parts = [
        b.locationName?.trim() || '—',
        `Lat, Long ${coord}`,
        g.distance ? `Distance ${g.distance}` : '',
    ].filter(Boolean);
    return parts.join(' | ');
}

/**
 * Always "Yes" or "No" for Excel — when Firestore has no value, uses the same defaults as Add / Edit Branch modal.
 */
function ynResolved(v: boolean | undefined | null, whenMissing: boolean): string {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    return whenMissing ? 'Yes' : 'No';
}

function rosterGuardCountOrZero(v: unknown): number {
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
    return Math.floor(v);
}

function eventAffectedExport(b: Branch): string {
    const note = String(b.eventAffectedAreasNote || '').trim();
    if (b.eventAffectedActive === true || note) return 'Yes';
    return 'No';
}

function atmMountTypeLabel(v: Branch['atmMountType']): string {
    if (v === 'wall_mounted') return 'Wall mounted';
    if (v === 'standalone') return 'Standalone';
    return '';
}

function sraLevelExportLabel(level: string | undefined): string {
    const raw = (level || 'green').toLowerCase();
    const map: Record<string, string> = {
        green: 'Green (Safest)',
        yellow: 'Yellow (Safe)',
        orange: 'Orange (Might be danger)',
        red: 'Red (Danger)',
    };
    return map[raw] || (raw ? raw : '');
}

function normCellKey(v: unknown): string {
    return String(v || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[^a-z0-9 ]+/g, '');
}

function rowToNormalizedMap(row: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(row || {})) {
        const nk = normCellKey(k);
        if (!nk) continue;
        out[nk] = v;
    }
    return out;
}

function strCell(v: any): string {
    if (v == null) return '';
    return String(v).trim();
}

function numCell(v: any): number | null {
    const raw = strCell(v).replace(/,/g, '');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function ynCell(v: any): boolean | null {
    const raw = strCell(v).toLowerCase();
    if (!raw) return null;
    if (['yes', 'y', 'true', '1'].includes(raw)) return true;
    if (['no', 'n', 'false', '0'].includes(raw)) return false;
    return null;
}

function cleanEmail(v: any): string {
    return strCell(v).toLowerCase();
}

function sraLevelImport(v: any): string | null {
    const raw = strCell(v).toLowerCase();
    if (!raw) return null;
    if (raw.includes('green')) return 'green';
    if (raw.includes('yellow')) return 'yellow';
    if (raw.includes('orange')) return 'orange';
    if (raw.includes('red')) return 'red';
    if (['g', 'y', 'o', 'r'].includes(raw)) {
        return raw === 'g' ? 'green' : raw === 'y' ? 'yellow' : raw === 'o' ? 'orange' : 'red';
    }
    return raw;
}

/** Full branch row for Excel — matches Branch Details / Add Branch modal fields. */
function branchToExportRow(
    branch: Branch,
    managers: Record<string, Manager>,
    isBankUser: boolean,
    deploymentsMap?: Record<string, { cctv?: string; bams?: string; guard?: string }>
): Record<string, string | number> {
    const g = branchTableGeo(branch);
    const latNum = g.lat ? Number(g.lat) : NaN;
    const lngNum = g.lng ? Number(g.lng) : NaN;
    const latDisp = !Number.isNaN(latNum) ? latNum.toFixed(5) : '';
    const lngDisp = !Number.isNaN(lngNum) ? lngNum.toFixed(5) : '';
    const psoLabel =
        branch.psoName && branch.psoName !== 'N/A' ? branch.psoName : 'No PSO';
    const branchTypeLine =
        branch.branchType === 'Other' && branch.branchTypeOther?.trim()
            ? `${branch.branchType || 'Branch'} — ${branch.branchTypeOther.trim()}`
            : branch.branchType || 'Branch';

    const bm = branch.branchManagerId ? managers[branch.branchManagerId] : undefined;
    const om = branch.operationalManagerId ? managers[branch.operationalManagerId] : undefined;
    const am = branch.areaManagerId ? managers[branch.areaManagerId] : undefined;

    const eventAffected =
        branch.eventAffectedActive === true ||
        !!(branch.eventAffectedAreasNote && String(branch.eventAffectedAreasNote).trim());

    const deactivationWhen = branch.deactivationDate
        ? (() => {
              try {
                  return new Date(branch.deactivationDate).toLocaleString();
              } catch {
                  return String(branch.deactivationDate);
              }
          })()
        : '';

    const branchDeps = deploymentsMap?.[branch.id];
    const cctvVendor = branchDeps?.cctv || 'N/A';
    const bamsVendor = branchDeps?.bams || 'N/A';
    const guardVendor = branchDeps?.guard || 'N/A';

    const row: Record<string, string | number> = {
        // Removed Branch, Location & position, Branch document ID, and Client Code as requested
        Bank: (branch.name || '').trim(),
        'Branch code': branch.branchCode || '',
        'Branch name': branch.address || '',
        'Branch Contact No.': branch.mobile != null ? String(branch.mobile).trim() : '',
        Region: branch.locationName || '',
        'Number of Guard': rosterGuardCountOrZero(branch.numberOfGuardsDay) + rosterGuardCountOrZero(branch.numberOfGuardsNight),
        'Branch Department': (branch.departments || []).join(', '),
        Latitude: latDisp,
        Longitude: lngDisp,
        'Distance from region': g.distance || '',

        'CCTV Vendor': cctvVendor,
        'BAMS Vendor': bamsVendor,
        'Guard Vendor': guardVendor,

        'Branch type': branchTypeLine,
        'Cash limit (M PKR)':
            branch.cashLimitMillion != null && !Number.isNaN(Number(branch.cashLimitMillion))
                ? Number(branch.cashLimitMillion)
                : '',

        // Yes / No (modal switches; missing Firestore → same defaults as modal)
        'Vantage point': ynResolved(branch.vantagePointAvailable, true),
        'Emergency exit': ynResolved(branch.emergencyExitAvailable, true),
        'Saturday open': ynResolved(branch.saturdayOpen, false),
        Locker: ynResolved(branch.lockerAvailable, true),
        'ATM available': ynResolved(branch.atmAvailable, false),
        'ATM type': branch.atmAvailable ? atmMountTypeLabel(branch.atmMountType) : '—',
        'ATM separate entrance': branch.atmAvailable
            ? ynResolved(branch.atmSeparateFromMainEntrance, true)
            : 'No',

        'Access control available': ynResolved(branch.accessControlAvailable, false),
        'Access control units': branch.accessControlAvailable && branch.accessControlUnits != null ? Number(branch.accessControlUnits) : '',
        'Access control locations': branch.accessControlAvailable && branch.accessControlLocations ? branch.accessControlLocations.join(', ') : '',

        'Covered area': (branch.coveredArea || '').trim(),
        Landmark: (branch.landmark || '').trim(),
        'Nearest other banks': (branch.nearestOtherBanksBranches || '').trim(),

        'Police station name': (branch.nearestPoliceStationName || '').trim(),
        'Police station address': (branch.nearestPoliceStationAddress || '').trim(),
        'Police station phone': (branch.nearestPoliceStationPhone || '').trim(),

        'SRA level': sraLevelExportLabel(branch.sraLevel),
        'SRA reason': (branch.sraReason || '').trim(),

        'Event affected': eventAffectedExport(branch),
        'Affected areas note': (branch.eventAffectedAreasNote || '').trim(),

        'Branch manager': bm?.name || '',
        'Branch manager email': bm?.email || '',
        'Branch manager mobile': bm?.mobile || '',
        'Manager Operation': om?.name || '',
        'Manager Operation email': om?.email || '',
        'Manager Operation mobile': om?.mobile || '',
        'Area Operation Manager': am?.name || '',
        'Area Operation Manager email': am?.email || '',
        'Area Operation Manager mobile': am?.mobile || '',

        'PSO name': psoLabel,
        'PSO email': (branch.psoEmail || '').trim(),
        'PSO contact': (branch.psoContact || '').trim(),

        Status: branch.isActive !== false ? 'Active' : 'Inactive',
        'Deactivation reason': (branch.deactivationReason || '').trim(),
        'Deactivation date': deactivationWhen,
    };

    if (isBankUser) {
        row['PSO ID'] = branch.psoId || '';
    }

    return row;
}

export default function Branches() {
    const [branches, setBranches] = useState<Branch[]>([]);
    const [filtered, setFiltered] = useState<Branch[]>([]);
    const [managers, setManagers] = useState<Record<string, Manager>>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [branchToDeactivate, setBranchToDeactivate] = useState<Branch | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<{
        total: number;
        processed: number;
        success: number;
        skipped: number;
        errors: number;
    }>({ total: 0, processed: 0, success: 0, skipped: 0, errors: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const [bankId, setBankId] = useState<string | null>(null);
    const [guardTempLookup, setGuardTempLookup] = useState<TemporaryGuardBranchLookup | null>(null);
    const [psoFilter, setPsoFilter] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [psos, setPsos] = useState<Array<{ id: string; name: string; email?: string; contact?: string; psoId?: string; designation?: string }>>([]);
    const [psoFilterOpen, setPsoFilterOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement | null>(null);
    const [importMenuOpen, setImportMenuOpen] = useState(false);
    const [sampleMenuOpen, setSampleMenuOpen] = useState(false);
    const [importType, setImportType] = useState<'full' | 'managers' | 'guards' | null>(null);
    const importMenuRef = useRef<HTMLDivElement | null>(null);
    const sampleMenuRef = useRef<HTMLDivElement | null>(null);
    const [exportProgress, setExportProgress] = useState<{ active: boolean; format: 'excel' | 'pdf'; percent: number; message: string; totalRows: number; processedRows: number; done: boolean }>({ active: false, format: 'excel', percent: 0, message: '', totalRows: 0, processedRows: 0, done: false });
    const cancelExportRef = useRef(false);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [draftStatusFilter, setDraftStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const [draftPsoFilter, setDraftPsoFilter] = useState<string[]>([]);
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((s) => s.user.pso);
    const showPsoColumn = useMemo(() => {
        if (isBankUser) return true;
        if (!pso?.designation) return false;
        const norm = pso.designation.trim().toLowerCase().replace(/_/g, ' ');
        const isHead = norm === 'head' || norm === 'head pso' || norm === 'regional head' || norm === 'regional pso';
        const isIncharge = designationIsIncharge(pso.designation);
        return isHead || isIncharge;
    }, [isBankUser, pso]);

    const psoFilterContainerRef = useRef<HTMLDivElement | null>(null);
    /** Only reset table page when user-driven filters change, not on every branches refresh. */
    const branchListFilterKeyRef = useRef<string | null>(null);

    useEffect(() => {
        const onDown = (e: Event) => {
            const target = e.target as Node;
            if (psoFilterContainerRef.current && !psoFilterContainerRef.current.contains(target)) {
                setPsoFilterOpen(false);
            }
            if (exportMenuRef.current && !exportMenuRef.current.contains(target)) {
                setExportMenuOpen(false);
            }
            if (importMenuRef.current && !importMenuRef.current.contains(target)) {
                setImportMenuOpen(false);
            }
            if (sampleMenuRef.current && !sampleMenuRef.current.contains(target)) {
                setSampleMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('touchstart', onDown, true);
        return () => {
            document.removeEventListener('mousedown', onDown, true);
            document.removeEventListener('touchstart', onDown, true);
        };
    }, []);

    const psoValueToName = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of psos) {
            const value = String((p as any).psoId || p.id);
            map.set(value, p.name || value);
        }
        return map;
    }, [psos]);

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                let fetchedBankId: string | null = null;

                if (isBankUser) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const { getDoc, doc } = await import('firebase/firestore');
                    const { db } = await import('../../../firebase/config');
                    const directBank = await getDoc(doc(db, "banks", uid));
                    if (directBank.exists()) {
                        fetchedBankId = uid;
                    } else {
                        // Try query by userId field
                        const { getDocs, collection } = await import('firebase/firestore');
                        const banksSnap = await getDocs(collection(db, "banks"));
                        for (const bankDoc of banksSnap.docs) {
                            const bankData = bankDoc.data();
                            if (bankData.userId === uid) {
                                fetchedBankId = bankDoc.id;
                                break;
                            }
                        }
                    }
                } else {
                    fetchedBankId = await fetchBranchBankId(uid);
                }

                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                        uid,
                        isBankUser,
                        pso?.authUid,
                        pso?.designation
                    );
                    setBranches(branchesData);
                    setFiltered(branchesData);
                    setManagers(managersData);

                    // Fetch PSOs for filter dropdown (only for bank users)
                    if (isBankUser) {
                        try {
                            const psoSnap = await getDocs(collection(db, 'banks', fetchedBankId, 'pos'));
                            const psoList = psoSnap.docs
                                .map(d => ({
                                    id: d.id,
                                    ...d.data()
                                } as { id: string; name: string; psoId?: string; designation?: string }))
                                .filter(pso => {
                                    const designation = (pso.designation || '').toLowerCase();
                                    return designation === 'pso' || !designation;
                                });
                            setPsos(psoList);
                        } catch (err) {
                            console.error('Error fetching PSOs:', err);
                        }
                    }
                }
            } catch (err: any) {
                showToast('Failed to load branches data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser, pso?.authUid, pso?.designation]);

    useEffect(() => {
        if (!bankId) {
            setGuardTempLookup(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const lk = await fetchApprovedTemporaryGuardLookupForBank(bankId);
                if (!cancelled) setGuardTempLookup(lk);
            } catch (e) {
                console.warn('Temporary guard lookup failed:', e);
                if (!cancelled) setGuardTempLookup(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bankId]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        const filterKey = [
            searchTerm,
            selectedFilter,
            psoFilter.join('\u001e'),
            startDate,
            endDate,
            isBankUser ? 'bank' : 'pso',
        ].join('|');

        let list = branches;

        // Status tab: Active / Inactive / All
        if (selectedFilter === 'active') {
            list = list.filter(branch => branch.isActive !== false);
        } else if (selectedFilter === 'inactive') {
            list = list.filter(branch => branch.isActive === false);
        }

        // Apply PSO filter (only for bank users)
        if (isBankUser && psoFilter.length > 0) {
            const wantsNoPso = psoFilter.includes('No PSO');
            const selectedPsos = psoFilter.filter((x) => x !== 'No PSO');
            if (wantsNoPso && selectedPsos.length === 0) {
                list = list.filter(branch => {
                    if (!branch.psoId || branch.psoId === '') {
                        return true;
                    }
                    const psoExists = psos.some(pso => {
                        const psoValue = (pso as any).psoId || pso.id;
                        return psoValue === branch.psoId;
                    });
                    return !psoExists;
                });
            } else {
                list = list.filter(branch => {
                    const branchHasPso = !!(branch.psoId && String(branch.psoId).trim());
                    if (!branchHasPso) return wantsNoPso;
                    if (selectedPsos.length === 0) return false;
                    return selectedPsos.includes(String(branch.psoId));
                });
            }
        }

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(branch => {
                const branchCode = (branch.branchCode || '').toString().toLowerCase();
                const branchName = (branch.address || '').toString().toLowerCase();
                return branchCode.includes(term) || branchName.includes(term);
            });
        }

        // Apply Date range filter
        if (startDate || endDate) {
            list = list.filter(branch => {
                if (!(branch as any).createdAt) return false;
                const createdAt = (branch as any).createdAt.toDate ? (branch as any).createdAt.toDate() : new Date((branch as any).createdAt);

                if (startDate && endDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    return createdAt >= start && createdAt <= end;
                } else if (startDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    return createdAt >= start;
                } else if (endDate) {
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    return createdAt <= end;
                }
                return true;
            });
        }

        // Sort by branch code alphabetically for all cases
        list.sort((a, b) => {
            const codeA = (a.branchCode || '').toString();
            const codeB = (b.branchCode || '').toString();
            return codeA.localeCompare(codeB);
        });

        setFiltered(list);
        if (branchListFilterKeyRef.current !== null && branchListFilterKeyRef.current !== filterKey) {
            setCurrentPage(1);
        }
        branchListFilterKeyRef.current = filterKey;
    }, [searchTerm, branches, selectedFilter, psoFilter, isBankUser, psos, startDate, endDate]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage) || 1);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const applySearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const openFilterModal = () => {
        setDraftStatusFilter(selectedFilter);
        setDraftPsoFilter(psoFilter);
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        setShowFilterModal(true);
    };

    const applyFilters = () => {
        setSelectedFilter(draftStatusFilter);
        setPsoFilter(draftPsoFilter);
        setStartDate(draftStartDate);
        setEndDate(draftEndDate);
        setCurrentPage(1);
        setShowFilterModal(false);
    };

    const clearFilters = () => {
        setSearchInput('');
        setSearchTerm('');
        
        setDraftStatusFilter('active');
        setDraftPsoFilter([]);
        setDraftStartDate('');
        setDraftEndDate('');

        setSelectedFilter('active');
        setPsoFilter([]);
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    const handleReset = () => {
        clearFilters();
    };

    const handlePDFExport = async () => {
        cancelExportRef.current = false;
        const total = filtered.length;
        setExportProgress({ active: true, format: 'pdf', percent: 10, message: 'Loading deployment data...', totalRows: total, processedRows: 0, done: false });
        try {
            const deploymentsMap = await loadDeploymentsForExport();
            if (cancelExportRef.current) {
                setExportProgress(p => ({ ...p, active: false }));
                return;
            }

            setExportProgress(p => ({ ...p, percent: 30, message: 'Generating PDF layout...' }));

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // Colors
            const primaryColor: [number, number, number] = [15, 23, 42]; // Slate 900
            const accentColor: [number, number, number] = [100, 116, 139]; // Slate 500

            // Header Banner
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.rect(0, 0, 297, 35, 'F');

            // Title
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(20);
            doc.text('PSO WEB - BRANCHES SUMMARY REPORT', 15, 18);

            // Metadata / Subtitle
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(200, 220, 255);
            const dateStr = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
            doc.text(`Generated: ${dateStr}`, 15, 26);

            // Filters Summary Box on header
            doc.setFillColor(30, 41, 59); // Dark slate
            doc.rect(170, 4, 112, 27, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.text('REPORT SUMMARY', 174, 9);
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(200, 200, 200);
            
            // Total Branches
            doc.text(`Total Branches: ${filtered.length}`, 174, 13.5);
            
            // Locations Count
            const uniqueLocs = Array.from(new Set(filtered.map(b => b.locationName).filter(Boolean)));
            doc.text(`Total Locations: ${uniqueLocs.length}`, 174, 17.5);
            
            // PSO Name(s)
            const psoNameDisplay = !isBankUser 
                ? (pso?.name || 'N/A') 
                : (psoFilter.length > 0 
                    ? psoFilter.map(id => psoValueToName.get(id) || id).join(', ') 
                    : 'All PSOs');
            const psoText = `PSO: ${psoNameDisplay}`;
            const truncatedPsoText = psoText.length > 32 ? psoText.substring(0, 32) + '...' : psoText;
            doc.text(truncatedPsoText, 174, 21.5);
            
            // Status Scope
            const statusLabel = selectedFilter.toUpperCase();
            doc.text(`Status Scope: ${statusLabel}`, 174, 25.5);

            // Calculate statistics
            const cctvStats: Record<string, number> = {};
            const bamsStats: Record<string, number> = {};
            const guardStats: Record<string, { branches: number; guards: number }> = {};
            const typeStats: Record<string, number> = {};
            const regionPsoStats: Record<string, { region: string; psoName: string; count: number }> = {};

            let totalCctvBranches = 0;
            let totalBamsBranches = 0;
            let totalGuardBranches = 0;
            let totalGuardCount = 0;
            let totalTypeBranches = 0;
            let totalBranchesFromRegions = 0;

            filtered.forEach((branch) => {
                const deps = deploymentsMap[branch.id] || {};
                const cctv = deps.cctv || 'N/A';
                const bams = deps.bams || 'N/A';
                const guard = deps.guard || 'N/A';

                const guards = rosterGuardCountOrZero(branch.numberOfGuardsDay) + rosterGuardCountOrZero(branch.numberOfGuardsNight);

                // CCTV
                cctvStats[cctv] = (cctvStats[cctv] || 0) + 1;
                totalCctvBranches++;

                // BAMS
                bamsStats[bams] = (bamsStats[bams] || 0) + 1;
                totalBamsBranches++;

                // Guarding
                if (!guardStats[guard]) {
                    guardStats[guard] = { branches: 0, guards: 0 };
                }
                guardStats[guard].branches += 1;
                guardStats[guard].guards += guards;
                totalGuardBranches++;
                totalGuardCount += guards;

                // Branch Type / Location Type
                const bType = branch.branchType || 'Branch';
                typeStats[bType] = (typeStats[bType] || 0) + 1;
                totalTypeBranches++;

                // Region & PSO Summary
                const regionName = branch.locationName || 'Unknown Region';
                const psoName = branch.psoName && branch.psoName !== 'N/A' ? branch.psoName : 'No PSO';
                const rpKey = `${regionName}__${psoName}`;
                if (!regionPsoStats[rpKey]) {
                    regionPsoStats[rpKey] = { region: regionName, psoName, count: 0 };
                }
                regionPsoStats[rpKey].count += 1;
                totalBranchesFromRegions += 1;
            });

            // Prepare Rows
            const cctvRows = Object.keys(cctvStats)
                .sort((a, b) => a.localeCompare(b))
                .map((vendor) => [vendor, cctvStats[vendor]]);
            cctvRows.push(['Grand Total', totalCctvBranches]);

            const bamsRows = Object.keys(bamsStats)
                .sort((a, b) => a.localeCompare(b))
                .map((vendor) => [vendor, bamsStats[vendor]]);
            bamsRows.push(['Grand Total', totalBamsBranches]);

            const guardRows = Object.keys(guardStats)
                .sort((a, b) => a.localeCompare(b))
                .map((vendor) => [vendor, guardStats[vendor].branches, guardStats[vendor].guards]);
            guardRows.push(['Grand Total', totalGuardBranches, totalGuardCount]);

            const typeRows = Object.keys(typeStats)
                .sort((a, b) => a.localeCompare(b))
                .map((type) => [type, typeStats[type]]);
            typeRows.push(['Grand Total', totalTypeBranches]);

            const regionPsoRows = Object.values(regionPsoStats)
                .sort((a, b) => a.region.localeCompare(b.region))
                .map((item) => [item.region, item.psoName, item.count]);
            regionPsoRows.push(['Grand Total', '', totalBranchesFromRegions]);

            // Table styling options matching the layout design
            const commonTableOptions = {
                theme: 'grid' as const,
                headStyles: {
                    fillColor: [230, 236, 244] as [number, number, number],
                    textColor: [51, 65, 85] as [number, number, number],
                    fontStyle: 'bold' as const,
                    fontSize: 8.5,
                    halign: 'left' as const
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: [51, 65, 85] as [number, number, number]
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252] as [number, number, number]
                },
                styles: {
                    lineColor: [226, 232, 240] as [number, number, number],
                    lineWidth: 0.2
                },
                didParseCell: (data: any) => {
                    if (data.row.raw[0] === 'Grand Total') {
                        data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index > 0) {
                        data.cell.styles.halign = 'right';
                    }
                }
            };

            // Start Drawing Tables Side-by-Side
            const startY = 45;

            // Table 1: CCTV Vendor
            autoTable(doc, {
                ...commonTableOptions,
                startY,
                margin: { left: 15 },
                tableWidth: 75,
                head: [['CCTV VENDOR', 'No. of Branches']],
                body: cctvRows,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 }
                }
            });
            const cctvFinalY = (doc as any).lastAutoTable.finalY;

            // Table 2: BAMS Vendor
            autoTable(doc, {
                ...commonTableOptions,
                startY,
                margin: { left: 95 },
                tableWidth: 75,
                head: [['BAMS VENDOR', 'No. of Branches']],
                body: bamsRows,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 }
                }
            });
            const bamsFinalY = (doc as any).lastAutoTable.finalY;

            // Table 3: Guarding Vendor
            autoTable(doc, {
                ...commonTableOptions,
                startY,
                margin: { left: 175 },
                tableWidth: 107,
                head: [['GUARDING VENDOR', 'No. of Branches', 'No. of Guards']],
                body: guardRows,
                columnStyles: {
                    0: { cellWidth: 52 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 30 }
                }
            });
            const guardFinalY = (doc as any).lastAutoTable.finalY;

            // Find the bottom Y position to place the next table below
            const nextStartY = Math.max(cctvFinalY, bamsFinalY, guardFinalY) + 10;

            // Table 4: Branch Type / Location Type
            autoTable(doc, {
                ...commonTableOptions,
                startY: nextStartY,
                margin: { left: 15 },
                tableWidth: 75,
                head: [['Location Type', 'No. of Locations']],
                body: typeRows,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 }
                }
            });

            // Table 5: Region & PSO Summary Table
            autoTable(doc, {
                ...commonTableOptions,
                startY: nextStartY,
                margin: { left: 95 },
                tableWidth: 187,
                head: [['REGION', 'PSO NAME', 'NO. OF BRANCHES']],
                body: regionPsoRows,
                columnStyles: {
                    0: { cellWidth: 60 },
                    1: { cellWidth: 97 },
                    2: { cellWidth: 30 }
                },
                didParseCell: (data: any) => {
                    if (data.row.raw[0] === 'Grand Total') {
                        data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 2) {
                        data.cell.styles.halign = 'right';
                    } else {
                        data.cell.styles.halign = 'left';
                    }
                }
            });

            // Footer function
            const pageCount = (doc as any).internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
                doc.text(
                    `Page ${i} of ${pageCount}`,
                    15,
                    doc.internal.pageSize.height - 10
                );
                doc.text(
                    'CONFIDENTIAL - FOR INTERNAL USE ONLY',
                    doc.internal.pageSize.width - 15 - 60,
                    doc.internal.pageSize.height - 10
                );
            }

            setExportProgress(p => ({ ...p, percent: 90, processedRows: total, message: 'Saving PDF file...' }));
            doc.save(`branches_summary_${new Date().toISOString().split('T')[0]}.pdf`);
            setExportProgress(p => ({ ...p, percent: 100, done: true, message: `PDF report with ${total} branches exported successfully!` }));
            showToast('PDF summary report generated successfully', 'success');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('Failed to generate PDF report', 'error');
            setExportProgress(p => ({ ...p, active: false }));
        }
    };

    const openDetailsModal = (branch: Branch, inEditMode: boolean = false) => {
        setSelectedBranch(branch);
        setEditMode(inEditMode);
        setShowDetailsModal(true);
    };

    const handleBranchAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);
            setCurrentPage(1);
            showToast('Branch added successfully', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing branches:', err);
            showToast('Failed to refresh branches', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = (branch: Branch) => {
        if (branch.isActive !== false) {
            setBranchToDeactivate(branch);
            setShowDeactivationModal(true);
        } else {
            handleReactivate(branch);
        }
    };

    const handleDeactivate = async (reason: string, date: string) => {
        if (!branchToDeactivate || !uid) return;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', branchToDeactivate.bankId, 'branch', branchToDeactivate.id);
            await updateDoc(branchRef, {
                isActive: false,
                deactivationReason: reason,
                deactivationDate: date,
            });

            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);

            setShowDeactivationModal(false);
            setBranchToDeactivate(null);
            showToast('Branch deactivated successfully', 'success');
        } catch (err: any) {
            console.error('Error deactivating branch:', err);
            showToast('Failed to deactivate branch', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReactivate = async (branch: Branch) => {
        if (!uid) return;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', branch.bankId, 'branch', branch.id);
            await updateDoc(branchRef, {
                isActive: true,
                deactivationReason: null,
                deactivationDate: null,
            });

            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);

            showToast('Branch reactivated successfully', 'success');
        } catch (err: any) {
            console.error('Error reactivating branch:', err);
            showToast('Failed to reactivate branch', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (branch: Branch) => {
        if (!isBankUser) return;
        setBranchToDelete(branch);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!branchToDelete || !bankId) return;

        setDeleting(true);
        try {
            await deleteDoc(doc(db, 'banks', bankId, 'branch', branchToDelete.id));

            const currentUid = uid || auth.currentUser?.uid || '';
            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                currentUid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);
            showToast('Branch deleted successfully', 'success');
        } catch (err: any) {
            console.error('Error deleting branch:', err);
            showToast('Failed to delete branch', 'error');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setBranchToDelete(null);
        }
    };

    const handleImportClick = (type: 'full' | 'managers' | 'guards') => {
        if (!isBankUser) return;
        setImportType(type);
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !bankId) return;

        const currentImportType = importType || 'full';

        setImporting(true);
        setImportProgress({ total: 0, processed: 0, success: 0, skipped: 0, errors: 0 });
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { defval: '' });

            if (!rows.length) {
                showToast('No data found in the Excel file', 'warning');
                return;
            }

            setImportProgress({ total: rows.length, processed: 0, success: 0, skipped: 0, errors: 0 });

            // Reference data for mapping user-friendly import columns -> IDs used in Firestore
            const fetches: Promise<any>[] = [
                getDocs(collection(db, 'banks', bankId, 'branch')),
            ];

            if (currentImportType === 'full' || currentImportType === 'managers') {
                fetches.push(getDocs(collection(db, 'banks', bankId, 'managers')));
            } else {
                fetches.push(Promise.resolve(null));
            }
            if (currentImportType === 'full') {
                fetches.push(getDocs(collection(db, 'locations')));
                fetches.push(getDocs(collection(db, 'banks', bankId, 'pos')));
            } else {
                fetches.push(Promise.resolve(null));
                fetches.push(Promise.resolve(null));
            }

            const [branchSnap, mgrSnap, locSnap, psoSnap] = await Promise.all(fetches);

            const locationNameToId = new Map<string, string>();
            const locationIdSet = new Set<string>();
            const locationsIndex: Array<{ id: string; name: string; norm: string }> = [];
            if (locSnap) {
                locSnap.forEach((d: any) => {
                    const data = d.data() as any;
                    const displayName = String(data?.name || '').trim();
                    const norm = normCellKey(displayName);
                    locationIdSet.add(d.id);
                    if (norm) {
                        locationNameToId.set(norm, d.id);
                        locationsIndex.push({ id: d.id, name: displayName, norm });
                    }
                });
            }

            const psoEmailToId = new Map<string, string>();
            const psoNameToId = new Map<string, string>();
            if (psoSnap) {
                psoSnap.forEach((d: any) => {
                    const data = d.data() as any;
                    const resolvedId = String(data?.psoId || d.id).trim();
                    const email = cleanEmail(data?.email);
                    const name = normCellKey(data?.name);
                    if (email) psoEmailToId.set(email, resolvedId);
                    if (name) psoNameToId.set(name, resolvedId);
                });
            }

            const managerEmailToId = new Map<string, string>();
            const managerNameToId = new Map<string, string>();
            if (mgrSnap) {
                mgrSnap.forEach((d: any) => {
                    const data = d.data() as any;
                    const email = cleanEmail(data?.email);
                    const name = normCellKey(data?.name);
                    if (email) managerEmailToId.set(email, d.id);
                    if (name) managerNameToId.set(name, d.id);
                });
            }

            const branchCodeToDocId = new Map<string, string>();
            if (branchSnap) {
                branchSnap.forEach((d: any) => {
                    const data = d.data() as any;
                    const code = normCellKey(data?.branchCode);
                    if (code) branchCodeToDocId.set(code, d.id);
                });
            }

            let successCount = 0;
            let skippedCount = 0;
            let errorCount = 0;
            const skippedRows: Array<Record<string, any>> = [];
            let processedCount = 0;
            let lastUiUpdateAt = 0;

            const maybeUpdateProgress = (force = false) => {
                const now = Date.now();
                if (!force && processedCount < rows.length && now - lastUiUpdateAt < 75 && processedCount % 5 !== 0) return;
                lastUiUpdateAt = now;
                setImportProgress({
                    total: rows.length,
                    processed: processedCount,
                    success: successCount,
                    skipped: skippedCount,
                    errors: errorCount,
                });
            };

            const ensureManagerId = async (args: {
                email?: string;
                name?: string;
                mobile?: string;
                type: string;
            }): Promise<string | undefined> => {
                const email = cleanEmail(args.email);
                const name = String(args.name || '').trim();
                const mobile = String(args.mobile || '').trim();

                let managerId = email ? managerEmailToId.get(email) : undefined;
                if (!managerId && name) {
                    const normName = normCellKey(name);
                    if (normName) managerId = managerNameToId.get(normName);
                }

                if (managerId) {
                    if (mobile) {
                        try {
                            const mgrRef = doc(db, 'banks', bankId, 'managers', managerId);
                            await updateDoc(mgrRef, { mobile });
                        } catch (e) {
                            console.error('Failed to update manager mobile:', e);
                        }
                    }
                    return managerId;
                }

                if (!email) return undefined;

                try {
                    const finalName = name || email;
                    const ref = await addDoc(collection(db, 'banks', bankId, 'managers'), {
                        name: finalName,
                        mobile,
                        email,
                        type: String(args.type || '').trim(),
                        createdAt: Timestamp.now(),
                    });
                    managerEmailToId.set(email, ref.id);
                    const normName = normCellKey(finalName);
                    if (normName) managerNameToId.set(normName, ref.id);
                    return ref.id;
                } catch (e) {
                    console.error('Failed to auto-create manager during branch import:', e);
                    return undefined;
                }
            };

            for (let i = 0; i < rows.length; i++) {
                const originalRow = rows[i];
                const row = rowToNormalizedMap(originalRow);
                try {
                    const rowNumber = i + 2;

                    const branchCode = strCell(row['branch code'] ?? row['branchcode'] ?? row['code']);
                    if (!branchCode) {
                        throw new Error('Branch code is required');
                    }

                    const existingId = branchCodeToDocId.get(normCellKey(branchCode));

                    if (currentImportType === 'full') {
                        const address = strCell(row['branch name'] ?? row['branchname'] ?? row['address'] ?? row['branch']);
                        if (!address) {
                            throw new Error('Branch name is required');
                        }

                        const mobile = strCell(
                            row['branch contact no.'] ??
                            row['branch contact no'] ??
                            row['contact no'] ??
                            row['mobile'] ??
                            row['phone'] ??
                            row['contact']
                        );
                        const securityCode = strCell(row['client code'] ?? row['security code'] ?? row['clientcode']);

                        const locationName = strCell(row['region'] ?? row['location name'] ?? row['location']);
                        const locationIdFromSheet = strCell(row['location id']);
                        const normLocationName = normCellKey(locationName);

                        let resolvedLocationId = '';
                        if (locationIdFromSheet) {
                            if (locationIdSet.has(locationIdFromSheet)) {
                                resolvedLocationId = locationIdFromSheet;
                            } else if (!locationName) {
                                throw new Error(`Unknown location id "${locationIdFromSheet}"`);
                            }
                        }

                        if (!resolvedLocationId && normLocationName) {
                            resolvedLocationId = locationNameToId.get(normLocationName) || '';
                        }

                        if (!resolvedLocationId && normLocationName) {
                            const matches = locationsIndex.filter(
                                (l) => l.norm.includes(normLocationName) || normLocationName.includes(l.norm)
                            );
                            if (matches.length === 1) {
                                resolvedLocationId = matches[0].id;
                            } else if (matches.length > 1) {
                                const shown = matches.slice(0, 8).map((m) => m.name).filter(Boolean);
                                const suffix = matches.length > shown.length ? ', ...' : '';
                                throw new Error(
                                    `Ambiguous location/region "${locationName}" (matches: ${shown.join(', ')}${suffix})`
                                );
                            } else {
                                throw new Error(`Unknown location/region "${locationName}"`);
                            }
                        }

                        if (!resolvedLocationId) {
                            throw new Error(
                                locationName
                                    ? `Unknown location/region "${locationName}"`
                                    : 'Region is required (location name)'
                            );
                        }

                        const psoEmail = cleanEmail(row['pso email'] ?? row['pso'] ?? row['psoemail']);
                        const psoName = strCell(row['pso name']);
                        const resolvedPsoId =
                            (psoEmail ? psoEmailToId.get(psoEmail) : undefined) ||
                            (psoName ? psoNameToId.get(normCellKey(psoName)) : undefined) ||
                            undefined;

                        const bmEmail = cleanEmail(row['branch manager email'] ?? row['branchmanager email'] ?? row['bm email']);
                        const omEmail = cleanEmail(row['manager operation email'] ?? row['operations manager email'] ?? row['operational manager email'] ?? row['om email']);
                        const amEmail = cleanEmail(row['area operation manager email'] ?? row['area manager email'] ?? row['am email']);

                        const bmName = strCell(row['branch manager'] ?? row['bm']);
                        const omName = strCell(row['manager operation'] ?? row['operations manager'] ?? row['operational manager'] ?? row['om']);
                        const amName = strCell(row['area operation manager'] ?? row['area manager'] ?? row['am']);

                        const bmMobile = strCell(row['branch manager mobile'] ?? row['branchmanagermobile'] ?? row['bm mobile']);
                        const omMobile = strCell(row['manager operation mobile'] ?? row['operations manager mobile'] ?? row['operational manager mobile'] ?? row['om mobile']);
                        const amMobile = strCell(row['area operation manager mobile'] ?? row['area manager mobile'] ?? row['am mobile']);

                        let branchManagerId =
                            (bmEmail ? managerEmailToId.get(bmEmail) : undefined) ||
                            (bmName ? managerNameToId.get(normCellKey(bmName)) : undefined);
                        let operationalManagerId =
                            (omEmail ? managerEmailToId.get(omEmail) : undefined) ||
                            (omName ? managerNameToId.get(normCellKey(omName)) : undefined);
                        let areaManagerId =
                            (amEmail ? managerEmailToId.get(amEmail) : undefined) ||
                            (amName ? managerNameToId.get(normCellKey(amName)) : undefined);

                        const lat = numCell(row['latitude'] ?? row['lat']);
                        const lng = numCell(row['longitude'] ?? row['long'] ?? row['lng']);
                        const distanceFromRegion = strCell(row['distance from region'] ?? row['distance']);

                        const atmAvailable = ynCell(row['atm available']);
                        const atmType = strCell(row['atm type']).toLowerCase();
                        const atmMountType =
                            atmType.includes('wall') ? 'wall_mounted' : atmType.includes('stand') ? 'standalone' : null;

                        const toBoolOrDefault = (cell: any, fallback: boolean) => {
                            const b = ynCell(cell);
                            return b == null ? fallback : b;
                        };

                        const sraLevel = sraLevelImport(row['sra level']);
                        const sraReason = strCell(row['sra reason']);

                        const eventAffected = ynCell(row['event affected']);
                        const eventAffectedAreasNote = strCell(row['affected areas note']);

                        const isActiveCell = ynCell(row['active'] ?? row['status']);
                        const statusText = strCell(row['status']).toLowerCase();
                        const isActive =
                            isActiveCell != null
                                ? isActiveCell
                                : statusText
                                    ? !(statusText.includes('inactive') || statusText === '0' || statusText === 'false')
                                    : true;

                        const cashLimit = numCell(row['cash limit m pkr'] ?? row['cash limit'] ?? row['cashlimit']);
                        const branchType = strCell(row['branch type']) || 'Branch';
                        const branchTypeOther = strCell(row['branch type other']);

                        const newBranch: Record<string, any> = {
                            branchCode,
                            address,
                            mobile,
                            locationId: resolvedLocationId,
                            securityCode,
                            isActive,
                            branchType: branchType || 'Branch',
                            createdAt: new Date(),
                            vantagePointAvailable: toBoolOrDefault(row['vantage point'], true),
                            emergencyExitAvailable: toBoolOrDefault(row['emergency exit'], true),
                            saturdayOpen: toBoolOrDefault(row['saturday open'], false),
                            lockerAvailable: toBoolOrDefault(row['locker'], true),
                            eventAffectedActive: eventAffected != null ? eventAffected : false,
                        };

                        if (branchType.toLowerCase() === 'other' && branchTypeOther) {
                            newBranch.branchTypeOther = branchTypeOther;
                        }
                        if (cashLimit != null) newBranch.cashLimitMillion = cashLimit;

                        if (lat != null || lng != null || distanceFromRegion) {
                            newBranch.location = {
                                lat: lat != null ? lat : null,
                                long: lng != null ? lng : null,
                                ...(distanceFromRegion ? { distanceFromRegion } : {}),
                            };
                        }

                        if (atmAvailable != null) {
                            newBranch.atmAvailable = atmAvailable;
                            if (atmAvailable) {
                                newBranch.atmMountType = atmMountType;
                                newBranch.atmSeparateFromMainEntrance = toBoolOrDefault(row['atm separate entrance'], true);
                            } else {
                                newBranch.atmMountType = null;
                                newBranch.atmSeparateFromMainEntrance = null;
                            }
                        }

                        if (sraLevel) {
                            newBranch.sraLevel = sraLevel;
                            if (sraLevel === 'orange' || sraLevel === 'red') {
                                newBranch.sraReason = sraReason || null;
                            } else {
                                newBranch.sraReason = null;
                            }
                        }

                        if (eventAffected === true || eventAffectedAreasNote) {
                            newBranch.eventAffectedAreasNote = eventAffectedAreasNote || null;
                        } else {
                            newBranch.eventAffectedAreasNote = null;
                        }

                        if (resolvedPsoId) {
                            newBranch.psoId = resolvedPsoId;
                        }

                        if (bmEmail || bmName) {
                            if (!branchManagerId) {
                                branchManagerId = await ensureManagerId({ email: bmEmail, name: bmName, mobile: bmMobile, type: 'BranchManager' });
                            }
                            if (branchManagerId) newBranch.branchManagerId = branchManagerId;
                            if (bmEmail) newBranch.branchManagerEmail = bmEmail;
                            if (bmName) newBranch.branchManagerName = bmName;
                        }
                        if (omEmail || omName) {
                            if (!operationalManagerId) {
                                operationalManagerId = await ensureManagerId({ email: omEmail, name: omName, mobile: omMobile, type: 'OperationalManager' });
                            }
                            if (operationalManagerId) newBranch.operationalManagerId = operationalManagerId;
                            if (omEmail) newBranch.operationalManagerEmail = omEmail;
                            if (omName) newBranch.operationalManagerName = omName;
                        }
                        if (amEmail || amName) {
                            if (!areaManagerId) {
                                areaManagerId = await ensureManagerId({ email: amEmail, name: amName, mobile: amMobile, type: 'AreaOperationManager' });
                            }
                            if (areaManagerId) newBranch.areaManagerId = areaManagerId;
                            if (amEmail) newBranch.areaManagerEmail = amEmail;
                            if (amName) newBranch.areaManagerName = amName;
                        }

                        if (existingId) {
                            await updateDoc(doc(db, 'banks', bankId, 'branch', existingId), newBranch);
                        } else {
                            await addDoc(collection(db, 'banks', bankId, 'branch'), newBranch);
                        }
                    } else if (currentImportType === 'managers') {
                        if (!existingId) {
                            throw new Error(`Branch with code "${branchCode}" not found`);
                        }

                        const bmEmail = cleanEmail(row['branch manager email'] ?? row['branchmanager email'] ?? row['bm email']);
                        const omEmail = cleanEmail(row['manager operation email'] ?? row['operations manager email'] ?? row['operational manager email'] ?? row['om email']);
                        const amEmail = cleanEmail(row['area operation manager email'] ?? row['area manager email'] ?? row['am email']);

                        const bmName = strCell(row['branch manager'] ?? row['bm']);
                        const omName = strCell(row['manager operation'] ?? row['operations manager'] ?? row['operational manager'] ?? row['om']);
                        const amName = strCell(row['area operation manager'] ?? row['area manager'] ?? row['am']);

                        const bmMobile = strCell(row['branch manager mobile'] ?? row['branchmanagermobile'] ?? row['bm mobile']);
                        const omMobile = strCell(row['manager operation mobile'] ?? row['operations manager mobile'] ?? row['operational manager mobile'] ?? row['om mobile']);
                        const amMobile = strCell(row['area operation manager mobile'] ?? row['area manager mobile'] ?? row['am mobile']);

                        let branchManagerId =
                            (bmEmail ? managerEmailToId.get(bmEmail) : undefined) ||
                            (bmName ? managerNameToId.get(normCellKey(bmName)) : undefined);
                        let operationalManagerId =
                            (omEmail ? managerEmailToId.get(omEmail) : undefined) ||
                            (omName ? managerNameToId.get(normCellKey(omName)) : undefined);
                        let areaManagerId =
                            (amEmail ? managerEmailToId.get(amEmail) : undefined) ||
                            (amName ? managerNameToId.get(normCellKey(amName)) : undefined);

                        const updateBranch: Record<string, any> = {};

                        if (bmEmail || bmName) {
                            if (!branchManagerId) {
                                branchManagerId = await ensureManagerId({ email: bmEmail, name: bmName, mobile: bmMobile, type: 'BranchManager' });
                            }
                            if (branchManagerId) updateBranch.branchManagerId = branchManagerId;
                            if (bmEmail) updateBranch.branchManagerEmail = bmEmail;
                            if (bmName) updateBranch.branchManagerName = bmName;
                        }
                        if (omEmail || omName) {
                            if (!operationalManagerId) {
                                operationalManagerId = await ensureManagerId({ email: omEmail, name: omName, mobile: omMobile, type: 'OperationalManager' });
                            }
                            if (operationalManagerId) updateBranch.operationalManagerId = operationalManagerId;
                            if (omEmail) updateBranch.operationalManagerEmail = omEmail;
                            if (omName) updateBranch.operationalManagerName = omName;
                        }
                        if (amEmail || amName) {
                            if (!areaManagerId) {
                                areaManagerId = await ensureManagerId({ email: amEmail, name: amName, mobile: amMobile, type: 'AreaOperationManager' });
                            }
                            if (areaManagerId) updateBranch.areaManagerId = areaManagerId;
                            if (amEmail) updateBranch.areaManagerEmail = amEmail;
                            if (amName) updateBranch.areaManagerName = amName;
                        }

                        if (Object.keys(updateBranch).length === 0) {
                            throw new Error('No manager details found in row');
                        }

                        await updateDoc(doc(db, 'banks', bankId, 'branch', existingId), updateBranch);
                    } else if (currentImportType === 'guards') {
                        if (!existingId) {
                            throw new Error(`Branch with code "${branchCode}" not found`);
                        }

                        const guardsDayCell = row['number of guards day'] ?? row['number of guards (day)'] ?? row['guards day'] ?? row['guards_day'] ?? row['day guards'] ?? row['day guard'] ?? row['guards'];
                        const guardsNightCell = row['number of guards night'] ?? row['number of guards (night)'] ?? row['guards night'] ?? row['guards_night'] ?? row['night guards'] ?? row['night guard'];

                        const guardsDay = numCell(guardsDayCell);
                        const guardsNight = numCell(guardsNightCell);

                        if (guardsDay == null && guardsNight == null) {
                            throw new Error('Number of guards (day or night) is required');
                        }

                        const updateBranch: Record<string, any> = {};
                        if (guardsDay != null) {
                            updateBranch.numberOfGuardsDay = guardsDay;
                        }
                        if (guardsNight != null) {
                            updateBranch.numberOfGuardsNight = guardsNight;
                        }

                        await updateDoc(doc(db, 'banks', bankId, 'branch', existingId), updateBranch);
                    }

                    successCount += 1;
                } catch (error) {
                    const reason = error instanceof Error ? error.message : String(error);
                    skippedCount += 1;
                    errorCount += 1;
                    skippedRows.push({
                        'Row number': i + 2,
                        'Branch code': strCell(row['branch code'] ?? row['branchcode'] ?? row['code']),
                        Reason: reason,
                        ...originalRow,
                    });
                    console.error('Failed to import branch row:', { reason, originalRow });
                } finally {
                    processedCount += 1;
                    maybeUpdateProgress();
                }
            }

            maybeUpdateProgress(true);

            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid || auth.currentUser?.uid || '',
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);

            if (skippedRows.length) {
                const ws = XLSX.utils.json_to_sheet(skippedRows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Skipped Rows');
                XLSX.writeFile(
                    wb,
                    `branch_import_skipped_${new Date().toISOString().split('T')[0]}.xlsx`
                );
            }

            showToast(
                `Import completed: ${successCount} imported/updated, ${skippedCount} skipped`,
                skippedCount ? 'warning' : 'success'
            );
        } catch (error) {
            console.error('Error importing branches:', error);
            showToast('Failed to import branches', 'error');
        } finally {
            setImporting(false);
            setImportType(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Fetch deployments mapping
    const loadDeploymentsForExport = async () => {
        if (!bankId) return {};
        try {
            const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
            const companiesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
            const companiesList = companiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Find unique global company IDs
            const uniqueGlobalCompanyIds = Array.from(new Set(
                companiesList
                    .map(c => (c as any).companyId)
                    .filter((id): id is string => typeof id === 'string' && !!id)
            ));

            // Fetch global company details in parallel
            const globalCompaniesMap: Record<string, any> = {};
            if (uniqueGlobalCompanyIds.length > 0) {
                const { getDoc, doc } = await import('firebase/firestore');
                const globalDocs = await Promise.all(
                    uniqueGlobalCompanyIds.map(id => getDoc(doc(db, "companies", id)).catch(e => {
                        console.error(`Error loading global company ${id}:`, e);
                        return null;
                    }))
                );
                globalDocs.forEach(gDoc => {
                    if (gDoc && gDoc.exists()) {
                        globalCompaniesMap[gDoc.id] = gDoc.data();
                    }
                });
            }

            const branchDeploymentsMap: Record<string, { cctv?: string; bams?: string; guard?: string }> = {};

            for (const dDoc of deploymentsSnap.docs) {
                const dData = dDoc.data();
                const branchId = dData.branchId;
                if (!branchId) continue;

                const company = companiesList.find(c => c.id === dData.companiesId);
                if (!company) continue;

                let companyType = (company as any).companyType || '';
                let companyName = (company as any).companyName || '';

                const globalId = (company as any).companyId;
                if (globalId && globalCompaniesMap[globalId]) {
                    const globalData = globalCompaniesMap[globalId];
                    companyType = companyType || globalData.companyType || '';
                    companyName = companyName || globalData.companyName || '';
                }

                if (!branchDeploymentsMap[branchId]) {
                    branchDeploymentsMap[branchId] = {};
                }

                const typeUpper = companyType.toUpperCase().trim();
                if (typeUpper === 'CCTV') {
                    branchDeploymentsMap[branchId].cctv = companyName;
                } else if (typeUpper === 'BAMS') {
                    branchDeploymentsMap[branchId].bams = companyName;
                } else if (typeUpper === 'GUARD' || typeUpper === 'GUARD COMPANY' || typeUpper.includes('GUARD')) {
                    branchDeploymentsMap[branchId].guard = companyName;
                }
            }
            return branchDeploymentsMap;
        } catch (err) {
            console.error("Error loading deployments:", err);
            return {};
        }
    };

    const handleExport = async () => {
        cancelExportRef.current = false;
        const total = branches.length;
        setExportProgress({ active: true, format: 'excel', percent: 2, message: 'Loading deployment data...', totalRows: total, processedRows: 0, done: false });

        const deploymentsMap = await loadDeploymentsForExport();
        if (cancelExportRef.current) { setExportProgress(p => ({ ...p, active: false })); return; }

        if (!total) {
            showToast('No data to export', 'info');
            setExportProgress(p => ({ ...p, active: false }));
            return;
        }

        // Process rows in batches so UI can update and cancel can fire
        const BATCH = 20;
        const exportData: Record<string, any>[] = [];
        let cancelled = false;

        for (let i = 0; i < total; i += BATCH) {
            if (cancelExportRef.current) { cancelled = true; break; }
            const end = Math.min(i + BATCH, total);
            for (let j = i; j < end; j++) {
                exportData.push(branchToExportRow(branches[j], managers, isBankUser, deploymentsMap));
            }
            const pct = Math.round(10 + ((end / total) * 75));
            setExportProgress(p => ({ ...p, percent: pct, processedRows: end, message: `Processing ${end} of ${total} branches...` }));
            // Yield to allow React re-render
            await new Promise(r => setTimeout(r, 0));
        }

        const finalCount = exportData.length;
        if (!finalCount) { setExportProgress(p => ({ ...p, active: false })); return; }

        setExportProgress(p => ({ ...p, percent: 90, message: cancelled ? `Cancelled — downloading ${finalCount} of ${total} rows...` : 'Writing Excel file...' }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const cols = Object.keys(exportData[0] || {}).map((key) => {
            let maxLen = key.length;
            exportData.forEach((row) => { const val = String(row[key] || ''); if (val.length > maxLen) maxLen = val.length; });
            return { wch: maxLen + 3 };
        });
        worksheet['!cols'] = cols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Branches');
        XLSX.writeFile(workbook, `branches_${new Date().toISOString().split('T')[0]}.xlsx`);

        setExportProgress(p => ({ ...p, percent: 100, processedRows: finalCount, done: true, message: cancelled ? `Partial export: ${finalCount} of ${total} branches downloaded` : `All ${total} branches exported successfully!` }));
        showToast(cancelled ? `Partial export: ${finalCount} rows` : 'Exported all branches successfully', cancelled ? 'warning' : 'success');
    };

    const handleDownloadSample = (type: 'full' | 'managers' | 'guards') => {
        if (type === 'full') {
            const sampleRows = [
                {
                    'Branch code': '1234',
                    'Branch name': 'Main Branch, City Center',
                    'Branch Contact No.': '03001234567',
                    Region: 'Karachi South',
                    'Client Code': 'ABC-123',
                    Latitude: '24.86073',
                    Longitude: '67.00114',
                    'Distance from region': '2.4 km',
                    'Branch type': 'Branch',
                    'Branch type other': '',
                    'Cash limit (M PKR)': '15',
                    'Vantage point': 'Yes',
                    'Emergency exit': 'Yes',
                    'Saturday open': 'No',
                    Locker: 'Yes',
                    'ATM available': 'Yes',
                    'ATM type': 'Wall mounted',
                    'ATM separate entrance': 'Yes',
                    'SRA level': 'Green (Safest)',
                    'SRA reason': '',
                    'Event affected': 'No',
                    'Affected areas note': '',
                    'PSO email': 'pso1@bank.com',
                    'Branch manager email': 'bm@bank.com',
                    'Branch manager mobile': '03001234567',
                    'Manager Operation email': 'om@bank.com',
                    'Manager Operation mobile': '03001234567',
                    'Area Operation Manager email': 'am@bank.com',
                    'Area Operation Manager mobile': '03001234567',
                    Status: 'Active',
                },
            ];

            const instructionsAoa = [
                ['Branches import template'],
                [''],
                ['Required columns'],
                ['- Branch code', '- Branch name', '- Region (location name)'],
                [''],
                ['Do not use IDs'],
                ['- Use Region (location name) instead of Location ID'],
                ['- Use PSO email instead of PSO ID (email will be mapped to PSO uid/psoId automatically)'],
                ['- Use manager emails for Branch/Manager Operation/Area Operation Manager'],
                [''],
                ['Allowed values'],
                ['- Yes/No accepted for switches (also true/false, 1/0)'],
                ['- ATM type: "Wall mounted" or "Standalone" (ignored if ATM available = No)'],
                ['- Status: Active / Inactive'],
                [''],
                ['Import behavior'],
                ['- If Branch code exists: record will be UPDATED'],
                ['- If Branch code not found: record will be CREATED'],
                ['- If a row fails: it will be SKIPPED and included in a "Skipped Rows" Excel report'],
            ];

            const wsSample = XLSX.utils.json_to_sheet(sampleRows);
            const wsInfo = XLSX.utils.aoa_to_sheet(instructionsAoa);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, wsSample, 'Branches');
            XLSX.utils.book_append_sheet(workbook, wsInfo, 'Instructions');
            XLSX.writeFile(workbook, 'branches_import_template.xlsx');
            showToast('Branches sample file downloaded', 'success');
        } else if (type === 'managers') {
            const sampleRows = [
                {
                    'Branch code': '1234',
                    'Branch manager': 'Ali Khan',
                    'Branch manager email': 'bm.khi@bank.com',
                    'Branch manager mobile': '0336-0886816',
                    'Manager Operation': 'Sana Ahmed',
                    'Manager Operation email': 'om.khi@bank.com',
                    'Manager Operation mobile': '0333-8449823',
                    'Area Operation Manager': 'Tariq Mahmood',
                    'Area Operation Manager email': 'am.khi@bank.com',
                    'Area Operation Manager mobile': '0300-1234567',
                },
            ];

            const instructionsAoa = [
                ['Managers import template'],
                [''],
                ['Required columns'],
                ['- Branch code (must match an existing branch in the system)'],
                [''],
                ['Manager columns (at least one is required)'],
                ['- Branch manager (Name), Branch manager email, Branch manager mobile'],
                ['- Manager Operation (Name), Manager Operation email, Manager Operation mobile'],
                ['- Area Operation Manager (Name), Area Operation Manager email, Area Operation Manager mobile'],
                [''],
                ['Import behavior'],
                ['- Managers will be resolved by email or name, and automatically created if email is provided.'],
                ['- Branch managers/operations/area managers will be linked to the matching branch.'],
            ];

            const wsSample = XLSX.utils.json_to_sheet(sampleRows);
            const wsInfo = XLSX.utils.aoa_to_sheet(instructionsAoa);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, wsSample, 'Managers');
            XLSX.utils.book_append_sheet(workbook, wsInfo, 'Instructions');
            XLSX.writeFile(workbook, 'managers_import_template.xlsx');
            showToast('Managers sample file downloaded', 'success');
        } else if (type === 'guards') {
            const sampleRows = [
                {
                    'Branch code': '1234',
                    'Number of guards (day)': 2,
                    'Number of guards (night)': 1,
                },
            ];

            const instructionsAoa = [
                ['Number of Guards import template'],
                [''],
                ['Required columns'],
                ['- Branch code (must match an existing branch in the system)'],
                ['- Number of guards (day) and/or Number of guards (night)'],
                [''],
                ['Import behavior'],
                ['- Updates the day/night guard count for the matching branch.'],
            ];

            const wsSample = XLSX.utils.json_to_sheet(sampleRows);
            const wsInfo = XLSX.utils.aoa_to_sheet(instructionsAoa);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, wsSample, 'Guards');
            XLSX.utils.book_append_sheet(workbook, wsInfo, 'Instructions');
            XLSX.writeFile(workbook, 'guards_import_template.xlsx');
            showToast('Guards sample file downloaded', 'success');
        }
    };

    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Calculate branch statistics
    const totalBranches = branches.length;
    const activeBranches = branches.filter(b => b.isActive !== false).length;
    const inactiveBranches = branches.filter(b => b.isActive === false).length;

    // Calculate percentages for graphs
    const activePercentage = totalBranches > 0 ? (activeBranches / totalBranches) * 100 : 0;
    const inactivePercentage = totalBranches > 0 ? (inactiveBranches / totalBranches) * 100 : 0;

    const branchTypeCounts = useMemo<Record<string, number>>(() => countBranchesByType(branches), [branches]);
    const branchTypeKpiRows = useMemo<BranchTypeKpiRow[]>(
        () => branchTypeKpiOrderedRows(branchTypeCounts),
        [branchTypeCounts]
    );

    const branchTypeCardGradients = [
        'from-sky-500 to-sky-600',
        'from-indigo-500 to-indigo-600',
        'from-violet-500 to-violet-600',
        'from-fuchsia-500 to-fuchsia-600',
        'from-amber-500 to-amber-600',
        'from-emerald-500 to-emerald-600',
        'from-cyan-500 to-cyan-600',
        'from-rose-500 to-rose-600',
    ] as const;

    const guardCellLine = (label: string, value: number) => (
        <div className="flex flex-wrap gap-x-1.5 gap-y-0.5">
            <span className="shrink-0 font-semibold text-gray-600 dark:text-gray-400">{label}</span>
            <span className="tabular-nums text-gray-900 dark:text-gray-100">{value}</span>
        </div>
    );

    const columns: Column<Branch>[] = [
        {
            header: 'Branch',
            width: '23%',
            render: (branch) => (
                <div className="space-y-1.5 text-sm">
                    <div>
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Code{' '}
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{branch.branchCode}</span>
                    </div>
                    <div
                        style={{
                            maxWidth: '280px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                        }}
                        className="text-gray-700 dark:text-gray-300"
                    >
                        {branch.address}
                    </div>
                    <div>
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Branch Contact No.{' '}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">{branch.mobile}</span>
                    </div>
                </div>
            ),
        },
        {
            header: 'Location & position',
            width: '22%',
            render: (branch) => {
                const g = branchTableGeo(branch);
                const coord =
                    g.lat && g.lng ? `${Number(g.lat).toFixed(5)}, ${Number(g.lng).toFixed(5)}` : '—';
                return (
                    <div className="space-y-1.5 text-sm">
                        <div className="font-medium text-gray-800 dark:text-gray-200">
                            {branch.locationName || '—'}
                        </div>
                        <div className="font-mono text-xs tabular-nums text-gray-600 dark:text-gray-400">
                            <span className="text-gray-500 dark:text-gray-500">Lat, Long </span>
                            {coord}
                        </div>
                        {g.distance ? (
                            <div className="text-gray-600 dark:text-gray-400">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Distance </span>
                                {g.distance}
                            </div>
                        ) : null}
                    </div>
                );
            },
        },
        {
            header: 'Client Code',
            accessor: 'securityCode',
            width: '9%',
            render: (branch) => (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                    {branch.securityCode}
                </span>
            )
        },
        {
            header: 'Guards',
            width: '13%',
            render: (branch) => {
                const dayDisp = rosterGuardCountOrZero(branch.numberOfGuardsDay);
                const nightDisp = rosterGuardCountOrZero(branch.numberOfGuardsNight);
                const temp = guardTempLookup
                    ? lookupTemporaryGuardCount(guardTempLookup, branch)
                    : 0;
                return (
                    <div className="space-y-1 text-[11px] leading-snug sm:text-xs">
                        {guardCellLine('Day Guard', dayDisp)}
                        {guardCellLine('Night Guard', nightDisp)}
                        {guardCellLine('Temp Guard', temp)}
                    </div>
                );
            },
        },
    ];

    if (showPsoColumn) {
        columns.push({
            header: 'PSO Details',
            width: '15%',
            render: (branch) => (
                <div className="flex flex-col text-[11px] leading-snug sm:text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {branch.psoName && branch.psoName !== 'N/A' ? branch.psoName : 'No PSO'}
                    </span>
                    {branch.psoEmail && branch.psoEmail !== 'N/A' && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                            {branch.psoEmail}
                        </span>
                    )}
                    {branch.psoContact && branch.psoContact !== 'N/A' && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {branch.psoContact}
                        </span>
                    )}
                </div>
            )
        });
    }

    columns.push({
        header: 'Status',
        align: 'center',
        width: '10%',
        render: (branch) => (
            isBankUser ? (
                <div className="flex flex-col gap-2">
                    <label className="relative inline-flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={branch.isActive !== false}
                            onChange={() => handleStatusToggle(branch)}
                            className="peer sr-only"
                            disabled={loading}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    </label>
                    {branch.isActive === false && branch.deactivationDate && (
                        <div className="flex flex-col text-left">
                            <span className="text-[10px] text-red-500">
                                {branch.deactivationReason}
                            </span>
                            <span className="text-[10px] text-gray-400">
                                {new Date(branch.deactivationDate).toLocaleDateString()}
                            </span>
                        </div>
                    )}
                </div>
            ) : (
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${branch.isActive !== false
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                    {branch.isActive !== false ? 'Active' : 'Inactive'}
                </span>
            )
        )
    });

    columns.push({
        header: 'Action',
        width: '10%',
        render: (branch) => (
            <div className="flex items-center gap-2">
                <button
                    onClick={() => openDetailsModal(branch)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                    title="View Details"
                >
                    <Icon icon={FiEye} className="h-4 w-4" />
                </button>
                {isBankUser && (
                    <>
                        <button
                            onClick={() => openDetailsModal(branch, true)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                            title="Edit"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleDeleteClick(branch)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-red-500 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-400 dark:focus:ring-red-400"
                            title="Delete"
                        >
                            <Icon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                    </>
                )}
            </div>
        )
    });

    if (loading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center py-16">
                <div className="rounded-2xl border border-gray-200 bg-white px-10 py-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                    <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent dark:border-blue-400"></div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Loading branches</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Please wait…</p>
                </div>
            </div>
        );
    }

    const emptyTableMessage = (() => {
        if (searchTerm) return 'No branches found matching your search';
        if (selectedFilter === 'active') return 'No active branches';
        if (selectedFilter === 'inactive') return 'No inactive branches';
        return 'No branches available';
    })();

    const hasExtraFilters = Boolean(
        searchTerm || (isBankUser && psoFilter.length > 0) || startDate || endDate || selectedFilter !== 'active'
    );

    return (
        <>
            <div className="space-y-8">
                <div className="border-b border-gray-200 pb-6 dark:border-gray-800 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Branches</h1>
                        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                            {isBankUser
                                ? 'View and manage branch records, assignments, and status across your network.'
                                : 'View branch details and status for your assigned locations.'}
                        </p>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 font-medium pb-0.5">
                        {filtered.length === branches.length ? (
                            <span>
                                <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{filtered.length}</span>
                                {' '}
                                {filtered.length === 1 ? 'branch' : 'branches'}
                            </span>
                        ) : (
                            <span>
                                Showing{' '}
                                <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{filtered.length}</span>
                                {' '}
                                of {branches.length}
                                {hasExtraFilters ? ' (with filters)' : ''}
                            </span>
                        )}
                    </div>
                </div>

                {/* Search and Filter Controls */}
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                        <div className="min-w-0 flex-1">
                            <input
                                type="text"
                                placeholder="Search by Branch Code or Branch name..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applySearch();
                                    }
                                }}
                                className="w-full h-11 rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            />
                        </div>
                        <Button onClick={applySearch} className="!h-11 !py-0 flex items-center gap-2">
                            <Icon icon={FiSearch} className="h-4 w-4" />
                            Search
                        </Button>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        {isBankUser && (
                            <>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                
                                {/* Sample Download Dropdown */}
                                <div className="relative" ref={sampleMenuRef}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSampleMenuOpen((o) => !o);
                                            setImportMenuOpen(false);
                                            setExportMenuOpen(false);
                                        }}
                                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <Icon icon={FiDownload} className="h-4 w-4" />
                                        Sample
                                        <Icon icon={FiChevronDown} className={`ml-1 h-4 w-4 shrink-0 transition-transform ${sampleMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {sampleMenuOpen && (
                                        <div className="absolute right-0 z-40 mt-1 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                            <button
                                                type="button"
                                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-blue-50 dark:text-gray-100 dark:hover:bg-blue-900/20"
                                                onClick={() => {
                                                    setSampleMenuOpen(false);
                                                    handleDownloadSample('full');
                                                }}
                                            >
                                                <Icon icon={FiFileText} className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">Branches Template</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Full branch details template</span>
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-blue-50 dark:text-gray-100 dark:hover:bg-blue-900/20"
                                                onClick={() => {
                                                    setSampleMenuOpen(false);
                                                    handleDownloadSample('managers');
                                                }}
                                            >
                                                <Icon icon={FiUsers} className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">Managers Template</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Branch code & managers details</span>
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-blue-50 dark:text-gray-100 dark:hover:bg-blue-900/20"
                                                onClick={() => {
                                                    setSampleMenuOpen(false);
                                                    handleDownloadSample('guards');
                                                }}
                                            >
                                                <Icon icon={FiShield} className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">Guards Template</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Branch code & guards count</span>
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Import Dropdown */}
                                <div className="relative" ref={importMenuRef}>
                                    <button
                                        type="button"
                                        disabled={importing}
                                        onClick={() => {
                                            setImportMenuOpen((o) => !o);
                                            setSampleMenuOpen(false);
                                            setExportMenuOpen(false);
                                        }}
                                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <Icon icon={FiUpload} className="h-4 w-4" />
                                        {importing ? 'Importing...' : 'Import'}
                                        <Icon icon={FiChevronDown} className={`ml-1 h-4 w-4 shrink-0 transition-transform ${importMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {importMenuOpen && (
                                        <div className="absolute right-0 z-40 mt-1 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                            <button
                                                type="button"
                                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                                onClick={() => {
                                                    setImportMenuOpen(false);
                                                    handleImportClick('full');
                                                }}
                                            >
                                                <Icon icon={FiFileText} className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">Import Branches</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Create/update full details</span>
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                                onClick={() => {
                                                    setImportMenuOpen(false);
                                                    handleImportClick('managers');
                                                }}
                                            >
                                                <Icon icon={FiUsers} className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">Import Managers</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Update manager details</span>
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                                onClick={() => {
                                                    setImportMenuOpen(false);
                                                    handleImportClick('guards');
                                                }}
                                            >
                                                <Icon icon={FiShield} className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">Import Guards</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Update day/night guard counts</span>
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        
                        {/* Export with options (Excel / PDF) */}
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    setExportMenuOpen((o) => !o);
                                    setImportMenuOpen(false);
                                    setSampleMenuOpen(false);
                                }}
                                className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Export
                                <Icon icon={FiChevronDown} className={`ml-1 h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {exportMenuOpen && (
                                <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            void handleExport();
                                        }}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white mr-2">XLS</span>
                                        <span>
                                            <span className="block font-medium">Excel workbook</span>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Auto column width</span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            void handlePDFExport();
                                        }}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white mr-2">PDF</span>
                                        <span>
                                            <span className="block font-medium">PDF report</span>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Professional Summary</span>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>

                        <Button variant="secondary" onClick={openFilterModal} className="flex h-11 items-center gap-2">
                            <Icon icon={FiFilter} className="h-4 w-4" />
                            Filters
                        </Button>
                        {hasExtraFilters && (
                            <Button variant="secondary" onClick={clearFilters} className="h-11">
                                Clear Filters
                            </Button>
                        )}
                        {isBankUser && (
                            <Button
                                onClick={() => setShowAddModal(true)}
                                className="flex h-11 items-center gap-2"
                            >
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Add Branch
                            </Button>
                        )}
                    </div>
                </div>


                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={emptyTableMessage}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />
            </div>

            {/* Filter branches modal */}
            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filter Branches"
                footer={
                    <div className="flex justify-between gap-2 px-6 py-4">
                        <Button variant="secondary" onClick={clearFilters} className="text-sm">
                            Clear All
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
                                Cancel
                            </Button>
                            <Button onClick={applyFilters}>
                                Apply Filters
                            </Button>
                        </div>
                    </div>
                }
            >
                <div className="space-y-4 px-6 py-5">
                    {/* Status Filter */}
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Status
                        </label>
                        <select
                            value={draftStatusFilter}
                            onChange={(e) => setDraftStatusFilter(e.target.value as any)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="all">All Statuses</option>
                        </select>
                    </div>

                    {/* PSO Filter (Bank users only) */}
                    {isBankUser && (
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                PSO
                            </label>
                            <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 p-2 dark:border-gray-700 dark:bg-black/20">
                                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                    <input
                                        type="checkbox"
                                        checked={draftPsoFilter.includes('No PSO')}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setDraftPsoFilter((prev) =>
                                                checked
                                                    ? Array.from(new Set([...prev, 'No PSO']))
                                                    : prev.filter((x) => x !== 'No PSO')
                                            );
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                    />
                                    <span className="text-sm text-gray-800 dark:text-gray-200">No PSO</span>
                                </label>
                                <div className="my-1.5 border-t border-gray-100 dark:border-gray-700" />
                                {psos.map((pso) => {
                                    const psoValue = String((pso as any).psoId || pso.id);
                                    const checked = draftPsoFilter.includes(psoValue);
                                    return (
                                        <label
                                            key={pso.id}
                                            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const on = e.target.checked;
                                                    setDraftPsoFilter((prev) =>
                                                        on
                                                            ? Array.from(new Set([...prev, psoValue]))
                                                            : prev.filter((x) => x !== psoValue)
                                                    );
                                                }}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                            />
                                            <span className="text-sm text-gray-800 dark:text-gray-200">
                                                {pso.name}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Date range picker */}
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Created Date Range
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                type="date"
                                value={draftStartDate}
                                onChange={(e) => setDraftStartDate(e.target.value)}
                                className="rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                placeholder="Start Date"
                            />
                            <input
                                type="date"
                                value={draftEndDate}
                                onChange={(e) => setDraftEndDate(e.target.value)}
                                className="rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                placeholder="End Date"
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Modals */}
            <AddBranchModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleBranchAdded}
                bankId={bankId || undefined}
            />

            <BranchDetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setEditMode(false);
                }}
                branch={selectedBranch}
                editMode={editMode}
                managers={managers}
                bankId={bankId || undefined}
                onUpdate={handleBranchAdded}
            />

            <Modal
                isOpen={importing}
                onClose={() => { }}
                title="Importing branches"
                closeOnBackdropClick={false}
            >
                {(() => {
                    const total = importProgress.total || 0;
                    const processed = importProgress.processed || 0;
                    const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                    return (
                        <div className="space-y-4">
                            <div className="flex items-end justify-between">
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                    {total > 0 ? (
                                        <span>
                                            Processing <span className="font-semibold">{processed}</span> /{' '}
                                            <span className="font-semibold">{total}</span>
                                        </span>
                                    ) : (
                                        <span>Preparing import…</span>
                                    )}
                                </div>
                                <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                                    {pct}%
                                </div>
                            </div>

                            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                <div
                                    className="h-full rounded-full bg-blue-600 transition-[width] duration-150 ease-linear dark:bg-blue-500"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                                <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-black/30">
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Total</div>
                                    <div className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">{total}</div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-black/30">
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Success</div>
                                    <div className="mt-1 text-lg font-semibold tabular-nums text-green-700 dark:text-green-400">{importProgress.success}</div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-black/30">
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Skipped</div>
                                    <div className="mt-1 text-lg font-semibold tabular-nums text-amber-700 dark:text-amber-400">{importProgress.skipped}</div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-black/30">
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Errors</div>
                                    <div className="mt-1 text-lg font-semibold tabular-nums text-red-700 dark:text-red-400">{importProgress.errors}</div>
                                </div>
                            </div>

                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                Please don’t close this window while the import is running.
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            <DeactivationModal
                isOpen={showDeactivationModal}
                onClose={() => {
                    setShowDeactivationModal(false);
                    setBranchToDeactivate(null);
                }}
                onConfirm={handleDeactivate}
                title="Deactivate Branch"
                message={`Are you sure you want to deactivate branch ${branchToDeactivate?.branchCode}? This will prevent any operations on this branch.`}
            />

            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setBranchToDelete(null);
                }}
                title="Delete Branch"
                closeOnBackdropClick={false}
            >
                <div>
                    <p className="mb-4 text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete branch <strong>{branchToDelete?.branchCode}</strong>? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setBranchToDelete(null);
                            }}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>
            {exportProgress.active && (
                <div className="pointer-events-auto fixed bottom-4 left-4 z-[100] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 dark:from-slate-900 dark:to-slate-800">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-white">
                                <Icon icon={FiDownload} className="h-4 w-4 opacity-90" />
                                <span className="text-sm font-semibold tracking-tight">
                                    {exportProgress.format === 'excel' ? 'Exporting Excel' : 'Exporting PDF'}
                                </span>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-cyan-300">{Math.round(exportProgress.percent)}%</span>
                        </div>
                    </div>
                    <div className="space-y-3 p-4">
                        <div className="flex flex-col gap-1 text-xs text-slate-600 dark:text-slate-300">
                            <p className="font-medium text-slate-800 dark:text-slate-200">{exportProgress.message}</p>
                            {exportProgress.totalRows > 0 && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Processed: <span className="font-semibold text-slate-700 dark:text-slate-300">{exportProgress.processedRows}</span> of <span className="font-semibold text-slate-700 dark:text-slate-300">{exportProgress.totalRows}</span> branches
                                </p>
                            )}
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, Math.max(2, exportProgress.percent))}%` }}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            {!exportProgress.done ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        cancelExportRef.current = true;
                                    }}
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition dark:border-red-900/30 dark:bg-red-950/20 dark:text-red-400 dark:hover:bg-red-900/30"
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setExportProgress(p => ({ ...p, active: false }));
                                    }}
                                    className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                                >
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
